// @flow

import * as React from "react";

import pubSub from "../../utils/pubsub";
import getYoutubePreviewImage from "../../utils/getYoutubePreviewImage";
import LoadYoutubeIframeAPI from "../../utils/LoadYoutubeIframeAPI";

import { type ImageType } from "../../types";
import Icon from "../atoms/Icon";

type PropsType = {
    thumbnail?: ImageType,
    caption?: string,
    video_url: string,
};

const youtubeURLToId = (url: string): ?string => {
    var regExp =
        /^.*((youtu.be\/)|(v\/)|(\/u\/\w\/)|(embed\/)|(watch\?))\??v?=?([^#&?]*).*/;
    var match = url.match(regExp);
    return match && match[7].length === 11 ? match[7] : null;
};
const gtmState = {};
const YoutubeVideo = (props: PropsType): React.Node => {
    let progressInterval = null;
    let player = null;
    const youtubeProgressHandler = (event: Object) => {
        if (player) {
            const progress = Math.floor(
                (100 * player.getCurrentTime()) / player.getDuration()
            );
            if (!gtmState.p25 && progress >= 25) {
                gtmState.p25 = true;
                pubSub.publish("GAEvent", {
                    category: "Video Views",
                    action: "Progress Viewed",
                    label: props.video_url,
                    value: 25,
                });
            } else if (!gtmState.p50 && progress >= 50) {
                gtmState.p50 = true;
                pubSub.publish("GAEvent", {
                    category: "Video Views",
                    action: "Progress Viewed",
                    label: props.video_url,
                    value: 50,
                });
            } else if (!gtmState.p75 && progress >= 75) {
                gtmState.p75 = true;
                pubSub.publish("GAEvent", {
                    category: "Video Views",
                    action: "Progress Viewed",
                    label: props.video_url,
                    value: 75,
                });
            } else if (
                !gtmState.end &&
                player.getDuration() - player.getCurrentTime() <= 20 &&
                progress >= 90
            ) {
                gtmState.end = true;
                pubSub.publish("GAEvent", {
                    category: "Video Views",
                    action: "Watch to End",
                    label: props.video_url,
                });
            }
        }
    };
    const youtubeEventHandler = (event: Object) => {
        if (event.data !== undefined && Number.isInteger(event.data)) {
            switch (event.data) {
                case 1:
                    pubSub.publish("GAEvent", {
                        category: "Video Views",
                        action: "Play",
                        label: props.video_url,
                    });
                    break;
                case 2:
                    pubSub.publish("GAEvent", {
                        category: "Video Views",
                        action: "Pause",
                        label: props.video_url,
                    });
                    break;

                default:
                    break;
            }
        }
        return;
    };

    const playerRef = React.useRef(null);
    const handlePlayButtonClick = () => {
        if (playerRef.current) {
            player = new window.YT.Player(playerRef.current, {
                playerVars: { autoplay: 1 },
                videoId: youtubeURLToId(props.video_url),
                events: {
                    onStateChange: youtubeEventHandler,
                    onReady: () => {
                        progressInterval = setInterval(
                            youtubeProgressHandler,
                            500
                        );
                    },
                },
            });
        }
    };
    React.useEffect((): Function => {
        LoadYoutubeIframeAPI();
        return () => {
            clearInterval(progressInterval);
        };
    }, [progressInterval]);

    return (
        <div className="youtube-video">
            <div className="youtube-video__player">
                <div className="youtube-video__player__inner" ref={playerRef}>
                    <button
                        onClick={handlePlayButtonClick}
                        className="youtube-video__play-button"
                    >
                        <div className="youtube-video__play-button__inner">
                            <Icon name="play" width="32px" />
                        </div>
                    </button>
                    <img
                        src={
                            (props.thumbnail && props.thumbnail.url) ||
                            getYoutubePreviewImage(props.video_url)
                        }
                        alt={
                            props.caption ||
                            (props.thumbnail && props.thumbnail.alt)
                        }
                        className="youtube-video__thumbnail"
                    />
                </div>
            </div>
            {props.thumbnail && props.thumbnail.caption ? (
                <div className="youtube-video__caption">
                    <p className="body-tiny">
                        <em>{props.thumbnail.caption}</em>
                    </p>
                </div>
            ) : null}
        </div>
    );
};

export default YoutubeVideo;
